import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

function GoalPlanningBoard({
  goals, householdGoals, chores, onMoveGoal, onRemoveGoal,
  onAddGoal, onComplete, onToggleChecklistItem
}) {
  const { t } = useTranslation();
  const bucketConfig = {
    0: { label: t('chores.buckets.today'), emoji: '📌' },
    1: { label: t('chores.buckets.tomorrow'), emoji: '📆' },
    2: { label: t('chores.buckets.thisWeek'), emoji: '📅' },
    3: { label: t('chores.buckets.thisMonth'), emoji: '🗓' },
    4: { label: t('chores.buckets.later'), emoji: '💤' }
  };
  const [viewMode, setViewMode] = useState('mine');

  const displayGoals = viewMode === 'mine' ? goals : householdGoals;

  // Group goals by bucket
  const buckets = {};
  Object.keys(bucketConfig).forEach(key => {
    buckets[key] = displayGoals.filter(g => g.bucket === parseInt(key));
  });

  // Chores not yet planned
  const plannedChoreIds = new Set(goals.map(g => g.choreId || g.chore?.id));
  const unplannedChores = chores.filter(c => c.status !== 2 && !plannedChoreIds.has(c.id));

  return (
    <div className="goal-board">
      <div className="goal-board-header">
        <h3>🎯 {t('chores.goals.title')}</h3>
        <div className="goal-view-toggle">
          <button
            className={`chore-filter-btn ${viewMode === 'mine' ? 'active' : ''}`}
            onClick={() => setViewMode('mine')}
          >
            {t('chores.goals.myPlan')}
          </button>
          <button
            className={`chore-filter-btn ${viewMode === 'household' ? 'active' : ''}`}
            onClick={() => setViewMode('household')}
          >
            {t('chores.goals.householdPlans')}
          </button>
        </div>
      </div>

      <div className="goal-buckets">
        {Object.entries(bucketConfig).map(([key, { label, emoji }]) => (
          <GoalBucket
            key={key}
            bucket={parseInt(key)}
            label={label}
            emoji={emoji}
            goals={buckets[key]}
            onMoveGoal={onMoveGoal}
            onRemoveGoal={onRemoveGoal}
            onComplete={onComplete}
            onToggleChecklistItem={onToggleChecklistItem}
            isHouseholdView={viewMode === 'household'}
          />
        ))}
      </div>

      {viewMode === 'mine' && unplannedChores.length > 0 && (
        <div className="goal-unplanned">
          <h4>📋 {t('chores.goals.unplannedChores')} ({unplannedChores.length})</h4>
          <p className="goal-unplanned-hint">{t('chores.goals.unplannedHint')}</p>
          <div className="goal-unplanned-list">
            {unplannedChores.map(chore => (
              <UnplannedChoreRow key={chore.id} chore={chore} onAddGoal={onAddGoal} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function GoalBucket({ bucket, label, emoji, goals, onMoveGoal, onRemoveGoal, onComplete, onToggleChecklistItem, isHouseholdView }) {
  const { t } = useTranslation();
  const overdueGoals = goals.filter(g => g.isOverdue);
  const normalGoals = goals.filter(g => !g.isOverdue);

  return (
    <div className={`goal-bucket ${goals.length === 0 ? 'goal-bucket-empty' : ''}`}>
      <div className="goal-bucket-header">
        <span>{emoji} {label}</span>
        <span className="goal-bucket-count">{goals.length}</span>
      </div>
      <div className="goal-bucket-items">
        {overdueGoals.map(goal => (
          <GoalItem
            key={goal.id}
            goal={goal}
            onMoveGoal={onMoveGoal}
            onRemoveGoal={onRemoveGoal}
            onComplete={onComplete}
            onToggleChecklistItem={onToggleChecklistItem}
            isHouseholdView={isHouseholdView}
          />
        ))}
        {normalGoals.map(goal => (
          <GoalItem
            key={goal.id}
            goal={goal}
            onMoveGoal={onMoveGoal}
            onRemoveGoal={onRemoveGoal}
            onComplete={onComplete}
            onToggleChecklistItem={onToggleChecklistItem}
            isHouseholdView={isHouseholdView}
          />
        ))}
        {goals.length === 0 && <div className="goal-bucket-placeholder">{t('chores.goals.noChoresPlanned')}</div>}
      </div>
    </div>
  );
}

function GoalItem({ goal, onMoveGoal, onRemoveGoal, onComplete, onToggleChecklistItem, isHouseholdView }) {
  const { t } = useTranslation();
  const bucketLabels = {
    0: t('chores.buckets.today'), 1: t('chores.buckets.tomorrow'),
    2: t('chores.buckets.thisWeek'), 3: t('chores.buckets.thisMonth'),
    4: t('chores.buckets.later')
  };
  const [showMoveMenu, setShowMoveMenu] = useState(false);
  const chore = goal.chore;
  const checklistItems = chore?.checklistItems?.$values || chore?.checklistItems || [];
  const completedItems = checklistItems.filter(ci => ci.isCompleted).length;
  const totalItems = checklistItems.length;

  return (
    <div className={`goal-item ${goal.isOverdue ? 'goal-item-overdue' : ''}`}>
      <div className="goal-item-header">
        {goal.isOverdue && <span className="goal-overdue-badge">⚠️ {t('chores.goals.overdue')}</span>}
        <span className="goal-item-title">{chore?.title || t('chores.goals.unknownChore')}</span>
        {isHouseholdView && goal.user && (
          <span className="goal-item-user">👤 {goal.user.name}</span>
        )}
      </div>

      {totalItems > 0 && (
        <div className="goal-item-checklist">
          <div className="chore-progress-bar chore-progress-bar-small">
            <div className="chore-progress-fill" style={{ width: `${(completedItems / totalItems) * 100}%` }}></div>
          </div>
          {checklistItems.map(item => (
            <label key={item.id} className={`chore-checklist-item chore-checklist-item-small ${item.isCompleted ? 'checked' : ''}`}>
              <input
                type="checkbox"
                checked={item.isCompleted}
                onChange={() => onToggleChecklistItem(chore.id, item.id, !item.isCompleted)}
                disabled={isHouseholdView}
              />
              <span>{item.title}</span>
            </label>
          ))}
        </div>
      )}

      {!isHouseholdView && (
        <div className="goal-item-actions">
          <button className="chore-btn chore-btn-success chore-btn-tiny" onClick={() => onComplete(chore?.id)} title={t('chores.goals.complete')}>✅</button>
          <div className="chore-goal-picker-wrapper">
            <button className="chore-btn chore-btn-secondary chore-btn-tiny" onClick={() => setShowMoveMenu(!showMoveMenu)} title={t('chores.goals.move')}>↕</button>
            {showMoveMenu && (
              <div className="chore-goal-picker">
                {Object.entries(bucketLabels).filter(([k]) => parseInt(k) !== goal.bucket).map(([key, label]) => (
                  <button
                    key={key}
                    className="chore-goal-picker-item"
                    onClick={() => { onMoveGoal(goal.id, parseInt(key)); setShowMoveMenu(false); }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button className="chore-btn chore-btn-danger chore-btn-tiny" onClick={() => onRemoveGoal(goal.id)} title={t('chores.goals.removeFromPlan')}>✕</button>
        </div>
      )}
    </div>
  );
}

function UnplannedChoreRow({ chore, onAddGoal }) {
  const { t } = useTranslation();
  const bucketLabels = {
    0: t('chores.buckets.today'), 1: t('chores.buckets.tomorrow'),
    2: t('chores.buckets.thisWeek'), 3: t('chores.buckets.thisMonth'),
    4: t('chores.buckets.later')
  };
  const [showPicker, setShowPicker] = useState(false);

  return (
    <div className="goal-unplanned-item">
      <span>{chore.title}</span>
      <div className="chore-goal-picker-wrapper">
        <button className="chore-btn chore-btn-secondary chore-btn-tiny" onClick={() => setShowPicker(!showPicker)}>
          🎯 {t('chores.goals.plan')}
        </button>
        {showPicker && (
          <div className="chore-goal-picker">
            {Object.entries(bucketLabels).map(([key, label]) => (
              <button
                key={key}
                className="chore-goal-picker-item"
                onClick={() => { onAddGoal(chore.id, parseInt(key)); setShowPicker(false); }}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default GoalPlanningBoard;
